import {
  useCallback,
  createContext,
  useEffect,
  useContext,
  useMemo,
  useState,
  type CSSProperties,
  type PropsWithChildren,
} from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Modal } from '../../components/ui/Modal';
import { ChevronRightIcon, PostVideoIcon } from '../../components/ui/icons';
import introductionVideo from '../../assets/videos/Introduction_Android.mp4';
import homePageVideo from '../../assets/videos/homePage_Android.mp4';
import './support.css';

type TutorialKey = 'introduction' | 'homePage';

interface MeetWeightLossBuddyContextValue {
  openTutorials: () => void;
}

const MeetWeightLossBuddyContext =
  createContext<MeetWeightLossBuddyContextValue | null>(null);

const VIDEO_SOURCES: Record<TutorialKey, string> = {
  introduction: introductionVideo,
  homePage: homePageVideo,
};

export function MeetWeightLossBuddyProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeTutorial, setActiveTutorial] = useState<TutorialKey | null>(null);
  const [feedCenter, setFeedCenter] = useState('50%');

  const alignWithFeed = useCallback((): void => {
    const feed = document.querySelector<HTMLElement>('.shell__content');
    if (!feed || window.innerWidth <= 768) {
      setFeedCenter('50%');
      return;
    }
    const bounds = feed.getBoundingClientRect();
    setFeedCenter(`${bounds.left + bounds.width / 2}px`);
  }, []);

  const value = useMemo(
    () => ({
      openTutorials: () => {
        alignWithFeed();
        setPickerOpen(true);
      },
    }),
    [alignWithFeed],
  );

  useEffect(() => {
    if (!pickerOpen && !activeTutorial) return;
    alignWithFeed();
    window.addEventListener('resize', alignWithFeed);
    return () => window.removeEventListener('resize', alignWithFeed);
  }, [activeTutorial, alignWithFeed, pickerOpen]);

  const openVideo = (tutorial: TutorialKey): void => {
    setPickerOpen(false);
    setActiveTutorial(tutorial);
  };
  const backdropStyle = {
    '--meet-wlb-feed-center': feedCenter,
  } as CSSProperties;

  return (
    <MeetWeightLossBuddyContext.Provider value={value}>
      {children}

      {pickerOpen && (
        <Modal
          title={t('sidebar.meetWlbModal.title')}
          labelledBy="meet-wlb-title"
          className="meet-wlb-modal"
          backdropClassName="meet-wlb-backdrop"
          backdropStyle={backdropStyle}
          onClose={() => setPickerOpen(false)}
        >
          <div className="meet-wlb-modal__body">
            <p className="meet-wlb-modal__subtitle">
              {t('sidebar.meetWlbModal.subtitle')}
            </p>
            <div className="meet-wlb-modal__options">
              {(['introduction', 'homePage'] as const).map(tutorial => (
                <button
                  key={tutorial}
                  type="button"
                  className="meet-wlb-modal__option"
                  onClick={() => openVideo(tutorial)}
                >
                  <span className="meet-wlb-modal__option-icon" aria-hidden>
                    <PostVideoIcon size={22} />
                  </span>
                  <span className="meet-wlb-modal__option-copy">
                    <strong>{t(`sidebar.meetWlbModal.${tutorial}`)}</strong>
                    <span>{t(`sidebar.meetWlbModal.${tutorial}Desc`)}</span>
                  </span>
                  <span className="meet-wlb-modal__watch">
                    {t('sidebar.meetWlbModal.watchNow')}
                    <ChevronRightIcon size={16} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </Modal>
      )}

      {activeTutorial && (
        <Modal
          title={t(`sidebar.meetWlbModal.${activeTutorial}`)}
          labelledBy="meet-wlb-video-title"
          className="meet-wlb-video"
          backdropClassName="meet-wlb-backdrop"
          backdropStyle={backdropStyle}
          onClose={() => setActiveTutorial(null)}
        >
          <div className="meet-wlb-video__body">
            <video
              className="meet-wlb-video__player"
              src={VIDEO_SOURCES[activeTutorial]}
              controls
              autoPlay
              playsInline
              preload="metadata"
              aria-label={t(`sidebar.meetWlbModal.${activeTutorial}`)}
            />
          </div>
        </Modal>
      )}
    </MeetWeightLossBuddyContext.Provider>
  );
}

export const useMeetWeightLossBuddy = (): MeetWeightLossBuddyContextValue => {
  const context = useContext(MeetWeightLossBuddyContext);
  if (!context) {
    throw new Error('useMeetWeightLossBuddy must be used within its provider');
  }
  return context;
};
